<template>
  <div>
    <!-- Toolbar de importação -->
    <v-card flat class="mb-4">
      <v-card-text class="py-3">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="mr-3">
            <v-icon color="primary" size="28">mdi-table-arrow-up</v-icon>
          </v-col>
          <v-col>
            <div class="text-subtitle-1 font-weight-medium">
              Atualização em massa
            </div>
            <div class="text-caption grey--text">
              Importe um arquivo XLSX para pré-visualizar e atualizar os
              produtos
            </div>
          </v-col>
          <v-col cols="auto">
            <ImportXlsxField
              url="product/parse-xlsx"
              @input="handleXlsx($event)"
              @file="xlsxFile = $event"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <template v-if="hasProducts">
      <!-- Controles de busca + ação -->
      <v-card flat class="mb-4 sticky-toolbar">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="5">
              <v-text-field
                v-model="searchText"
                label="Buscar por nome"
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
                dense
                outlined
                background-color="white"
                @input="handleSearchInput(searchText)"
                @click:clear="handleSearchInput('')"
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="searchCode"
                label="Código"
                prepend-inner-icon="mdi-barcode"
                clearable
                hide-details
                dense
                outlined
                @input="handleSearchCode(searchCode)"
                @click:clear="handleSearchCode('')"
              />
            </v-col>
            <v-col cols="12" sm="4" class="d-flex align-center justify-end">
              <span class="text-caption grey--text mr-4">
                {{ products.length }} produto{{
                  products.length !== 1 ? "s" : ""
                }}
                exibido{{ products.length !== 1 ? "s" : "" }}
              </span>
              <v-btn
                color="success"
                :disabled="processing"
                :loading="processing"
                depressed
                @click="updateProducts()"
              >
                <v-icon left small>mdi-cloud-upload-outline</v-icon>
                Atualizar produtos
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Tabela -->
      <v-card flat>
        <v-data-table
          :headers="headers"
          :items="products"
          :items-per-page="-1"
          :mobile-breakpoint="0"
          hide-default-footer
          disable-sort
          dense
        >
            <!-- Código da Marca (Texto) -->
          <template v-slot:[`item.new_brand_code`]="{ item }">
            <InfoTextCell
              :primary="item.new_brand_code"
              :secondary="item.brand_id"
            />
          </template>

            <!-- Código do Fornecedor (Texto) -->
          <template v-slot:[`item.supplier_code`]="{ item }">
            <InfoTextCell
              :primary="item.new_supplier_code"
              :secondary="item.supplier_id"
            />
          </template>

            <!-- Código da Categoria (Texto) -->
          <template v-slot:[`item.new_category_code`]="{ item }">
            <InfoTextCell
              :primary="item.new_category_code"
              :secondary="item.category_id"
            />
          </template>

            <!-- Preço de Compra (Decimal) -->
          <template v-slot:[`item.new_purchase_price`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_purchase_price"
              :secondary="item.product_variants[0].purchase_price"
            />
          </template>

            <!-- ST (Decimal) -->
          <template v-slot:[`item.new_st`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_st"
              :secondary="item.product_variants[0].st"
            />
          </template>

            <!-- IPI (Decimal) -->
          <template v-slot:[`item.new_ipi`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_ipi"
              :secondary="item.product_variants[0].ipi"
            />
          </template>


            <!-- Frete (Decimal) -->
          <template v-slot:[`item.new_shipping`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_shipping"
              :secondary="item.product_variants[0].shipping"
            />
          </template>

            <!-- Custo (Decimal) -->
          <template v-slot:[`item.new_cost`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_cost"
              :secondary="item.product_variants[0].cost"
            />
          </template>

            <!-- ICMS Débito (Decimal) -->
          <template v-slot:[`item.new_icms_debt`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_icms_debt"
              :secondary="item.product_variants[0].icms_debt"
            />
          </template>

            <!-- ICMS Crédito (Decimal) -->
          <template v-slot:[`item.new_icms_credit`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_icms_credit"
              :secondary="item.product_variants[0].icms_credit"
            />
          </template>

            <!-- PIS Entrada (Decimal) -->
          <template v-slot:[`item.new_pis_credit`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_pis_credit"
              :secondary="item.product_variants[0].pis_credit"
            />
          </template>

            <!-- PIS Saída (Decimal) -->
          <template v-slot:[`item.new_pis`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_pis"
              :secondary="item.product_variants[0].pis"
            />
          </template>

            <!-- COFINS Entrada (Decimal) -->
          <template v-slot:[`item.new_cofins_credit`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_cofins_credit"
              :secondary="item.product_variants[0].cofins_credit"
            />
          </template>

            <!-- COFINS Saída (Decimal) -->
          <template v-slot:[`item.new_cofins`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_cofins"
              :secondary="item.product_variants[0].cofins"
            />
          </template>




            <!-- Comissão (Decimal) -->
          <template v-slot:[`item.new_commission`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_commission"
              :secondary="item.product_variants[0].commission"
            />
          </template>

            <!-- Preço à Vista (Decimal) -->
          <template v-slot:[`item.new_price_cash`]="{ item }">
            <InfoDecimalCell
              :primary="item.new_price_cash"
              :secondary="item.product_variants[0].price_cash"
            />
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- Dialog de progresso -->
    <v-dialog v-model="processing" persistent max-width="460">
      <v-card>
        <v-card-title class="d-flex align-center pb-1 pt-4 px-6">
          <v-icon color="success" class="mr-2">mdi-cloud-upload-outline</v-icon>
          <span class="text-subtitle-1 font-weight-medium"
            >Atualizando produtos</span
          >
        </v-card-title>

        <v-card-text class="px-6 pb-5">
          <div class="d-flex align-center mb-4">
            <div class="progress-circle-wrap mr-5">
              <v-progress-circular
                :value="progress"
                color="success"
                size="72"
                width="6"
                rotate="-90"
              >
                <span class="text-body-2 font-weight-bold"
                  >{{ progress }}%</span
                >
              </v-progress-circular>
            </div>
            <div>
              <div class="text-body-2 grey--text text--darken-2 mb-1">
                Lotes processados
              </div>
              <div class="text-h6 font-weight-bold">
                {{ processedJobs }}
                <span class="text-body-1 grey--text font-weight-regular"
                  >/ {{ totalJobs }}</span
                >
              </div>
              <div v-if="hasFailed" class="d-flex align-center mt-2">
                <v-icon color="warning" small class="mr-1"
                  >mdi-alert-outline</v-icon
                >
                <span class="text-caption warning--text"
                  >Algumas falhas detectadas</span
                >
              </div>
            </div>
          </div>

          <v-progress-linear
            :value="progress"
            :color="hasFailed ? 'warning' : 'success'"
            height="6"
            rounded
            background-color="grey lighten-3"
          />

          <div class="text-caption grey--text mt-3">
            Não feche esta janela. O processo será concluído em breve.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ImportXlsxField from "@/components/product/ui/ImportXlsxField.vue";
import InfoDecimalCell from "@/components/mass-att-table/InfoDecimalCell.vue";
import InfoTextCell from "@/components/mass-att-table/InfoTextCell.vue";

export default {
  components: { ImportXlsxField, InfoDecimalCell, InfoTextCell },

  data() {
    return {
      products: [],
      allProducts: [],
      xlsxFile: null,
      searchText: "",
      searchCode: "",
      processing: false,
      operationId: null,
      totalJobs: 0,
      processedJobs: 0,
      progress: 0,
      hasFailed: false,
      pollInterval: null,
      headers: [
        { align: "center", value: "id", text: "ID" },
        { align: "center", value: "code", text: "COD" },
        { value: "name", text: "Nome", width: "500px" },
        { align: "center", value: "new_purchase_price", text: "P.Compra" },
        { align: "center", value: "new_price_cash", text: "P. à Vista" },
        { align: "center", value: "new_commission", text: "Comissão" },
        { align: "center", value: "new_shipping", text: "Frete" },
        { align: "center", value: "new_cost", text: "Custo" },
        { align: "center", value: "new_ipi", text: "IPI" },
        { align: "center", value: "new_st", text: "ST" },
        { align: "center", value: "new_pis_credit", text: "PIS Entrada" },
        { align: "center", value: "new_pis", text: "PIS Saída" },
        { align: "center", value: "new_cofins_credit", text: "COFINS Entrada" },
        { align: "center", value: "new_cofins", text: "COFINS Saída" },
        { value: "new_ncm", text: "NCM" },
        { value: "new_cest", text: "CEST" },
        { align: "center", value: "new_icms_debt", text: "ICMS Saída" },
        { align: "center", value: "new_icms_credit", text: "ICMS Entrada" },
        { value: "new_short_name", text: "Nome curto", width: "300px" },
        { value: "new_origem", text: "Origem" },
        { value: "new_classificacao_fiscal", text: "Classif. Fiscal" },
        { value: "new_brand_code", text: "Cod. Marca" },
        { value: "supplier_code", text: "Cod. Fornecedor" },
        { value: "new_category_code", text: "Cod. Categoria" },
        { align: "center", value: "new_status", text: "Status" },
      ],
    };
  },

  computed: {
    hasProducts() {
      return this.allProducts.length > 0;
    },
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    handleXlsx(data) {
      this.allProducts = JSON.parse(JSON.stringify(data.products));
      this.products = [...this.allProducts];
    },

    updateProducts() {
      if (!this.xlsxFile) return;

      this.$loading.start();

      const form = new FormData();
      form.append("xlsx", this.xlsxFile, "xlsx");

      this.$http
        .store("product/mass-update-v2", form)
        .then((response) => {
          this.$loading.finish();
          this.startBatchTracking(response);
        })
        .catch(() => {
          this.$loading.finish();
        });
    },

    startBatchTracking(response) {
      this.operationId = response.operation_id;
      this.processedJobs = 0;
      this.progress = 0;
      this.hasFailed = false;
      this.processing = true;
      this.pollInterval = setInterval(this.pollBatchStatus, 2000);
    },

    async pollBatchStatus() {
      try {
        const status = await this.$http.show(
          "product/mass-update-v2",
          this.operationId,
        );

        if (status.status === "pending") return;

        if (status.status === "failed") {
          this.stopPolling();
          this.$snackbar({
            message: "Falha ao processar o arquivo. Tente novamente.",
            props: { color: "error", timeout: 10000 },
          });
          return;
        }

        this.totalJobs = status.total_jobs;
        this.processedJobs = status.processed_jobs;
        this.progress = status.progress;
        this.hasFailed = status.has_failures;

        if (status.finished || status.cancelled) {
          this.stopPolling();
          this.$snackbar({
            message: status.has_failures
              ? "Atualização concluída com falhas em alguns lotes."
              : "Produtos atualizados com sucesso!",
            props: {
              color: status.has_failures ? "warning" : "success",
              timeout: 10000,
            },
          });
          this.$router.push("/produtos");
        }
      } catch {
        this.stopPolling();
      }
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      this.processing = false;
    },

    handleSearchInput(text) {
      this.searchCode = "";
      if (!text) return (this.products = [...this.allProducts]);
      this.products = this.allProducts.filter((p) =>
        p.name.toLowerCase().includes(text.toLowerCase().trim()),
      );
    },

    handleSearchCode(text) {
      this.searchText = "";
      if (!text) return (this.products = [...this.allProducts]);
      this.products = this.allProducts.filter((p) => p.code == text.trim());
    },
  },
};
</script>

<style scoped>
.sticky-toolbar {
  position: sticky;
  top: 64px;
  z-index: 4;
  background: white;
}
</style>
